import { go, goReplace, hashQuery } from '../../App';
import { useLoad, usePolled } from '../../data';
import { MonthCalendar, PageHead, monthWindow } from '../../ui';
import { Account, Booking } from '../../types';

// Calendar (canon 09 hotel "Calendar" · Register template, Full grid). The
// month of bookings across every supplier, at a glance. No new endpoint — it
// windows GET /v1/bookings by the byStart GSI (from/to), the same read the
// Bookings register uses, and each chip opens the EXISTING /bookings/{id}
// drawer (changing module, so a full-page nav is canon-correct). Bookings only:
// this is the hotel's own booked days, not a fan-out of every supplier's free
// slots — that would fragment the one calendar the hotel actually needs. The
// grid itself is the shared MonthCalendar (canon 04, built once).

export function Calendar() {
  const account = useLoad<Account>('/account');
  const tz = account.data?.timezone ?? 'Europe/Athens';
  // "This month" is the property's month, not the device's.
  const thisMonth = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit' })
    .format(new Date())
    .slice(0, 7);
  const todayStr = new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(new Date());

  const q = hashQuery().get('m');
  const m = q && /^\d{4}-\d{2}$/.test(q) ? q : thisMonth;

  // Fetch exactly the six-week window the grid will show, so a trailing/leading
  // day still carries its jobs.
  const { from, to } = monthWindow(m);
  const { data, error } = usePolled<{ items: Booking[] }>(`/bookings?from=${from}&to=${to}`);
  const items = data ? data.items : null;

  const setMonth = (next: string) => goReplace(`/calendar?m=${next}`);

  return (
    <div>
      <PageHead title="Calendar" sub="Every booking, across your suppliers.">
        <a href="#/bookings/new" className="btn" style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}>
          New booking
        </a>
      </PageHead>

      {error && !items && <p className="error-line">{error}</p>}

      <MonthCalendar
        month={m}
        thisMonth={thisMonth}
        todayStr={todayStr}
        bookings={items}
        onMonth={setMonth}
        onOpenBooking={(b) => go(`/bookings/${b.id}`)}
        chipTitle={(b) => `${b.productName ?? b.productId} · ${b.guestName}`}
      />
    </div>
  );
}
